// src/components/portal/BusinessDateCalendar.js
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { C } from '../../styles/theme';

// ─── Brand tokens ─────────────────────────────────────────────────────────────
const FONT = "'Plus Jakarta Sans', sans-serif";

// ─── Helpers ──────────────────────────────────────────────────────────────────
// Build a local YYYY-MM-DD key (never use toISOString — it shifts to UTC).
const toKey = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const formatDisplayDate = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
};

// Monday-first weekday offset for the 1st of a month.
// JS getDay(): 0=Sun .. 6=Sat. We want Mon=0 .. Sun=6.
const mondayOffset = (jsDay) => (jsDay + 6) % 7;

// Monday-first weekday header translation keys (Mo..Su).
const WEEKDAY_KEYS = [
    'eod_cal_wd_mon', 'eod_cal_wd_tue', 'eod_cal_wd_wed',
    'eod_cal_wd_thu', 'eod_cal_wd_fri', 'eod_cal_wd_sat', 'eod_cal_wd_sun',
];

const BusinessDateCalendar = ({ value, availableDates, onChange }) => {
    const { t } = useLanguage() || { t: (k) => k };

    const [open, setOpen] = useState(false);
    // The month currently displayed in the grid — anchored to a Date on day 1.
    const [viewMonth, setViewMonth] = useState(() => {
        const base = value ? new Date(value + 'T00:00:00') : new Date();
        return new Date(base.getFullYear(), base.getMonth(), 1);
    });

    const wrapRef = useRef(null);

    const available = useMemo(
        () => (availableDates instanceof Set ? availableDates : new Set(availableDates || [])),
        [availableDates],
    );

    // Reset the displayed month to the selected value's month each time the popover opens.
    const openPopover = useCallback(() => {
        const base = value ? new Date(value + 'T00:00:00') : new Date();
        setViewMonth(new Date(base.getFullYear(), base.getMonth(), 1));
        setOpen(true);
    }, [value]);

    const toggle = useCallback(() => {
        if (open) setOpen(false);
        else openPopover();
    }, [open, openPopover]);

    // Outside-click + Escape — listeners only attached while open, cleaned up on close/unmount.
    useEffect(() => {
        if (!open) return;
        const onMouseDown = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        const onKeyDown = (e) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('mousedown', onMouseDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onMouseDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [open]);

    const goPrev = () => setViewMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1));
    const goNext = () => setViewMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1));

    const handlePick = (key) => {
        if (!available.has(key)) return;
        onChange(key);
        setOpen(false);
    };

    // Build the grid cells for the displayed month.
    const cells = useMemo(() => {
        const year = viewMonth.getFullYear();
        const month = viewMonth.getMonth();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const lead = mondayOffset(new Date(year, month, 1).getDay());
        const out = [];
        for (let i = 0; i < lead; i++) out.push(null);
        for (let d = 1; d <= daysInMonth; d++) {
            out.push(toKey(new Date(year, month, d)));
        }
        return out;
    }, [viewMonth]);

    const monthLabel = viewMonth.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

    const triggerLabel = value ? formatDisplayDate(value) : t('eod_cal_no_date');

    return (
        <div ref={wrapRef} style={{ position: 'relative', display: 'inline-block', fontFamily: FONT }}>
            {/* Trigger */}
            <button
                type="button"
                onClick={toggle}
                aria-haspopup="dialog"
                aria-expanded={open}
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    background: C.white,
                    border: `1px solid ${C.border}`,
                    borderRadius: 8,
                    padding: '8px 12px',
                    fontSize: 13,
                    fontWeight: 700,
                    color: value ? C.text : C.mutedLight,
                    cursor: 'pointer',
                    fontFamily: FONT,
                }}
            >
                <span aria-hidden="true" style={{ fontSize: 14, lineHeight: 1 }}>📅</span>
                <span>{triggerLabel}</span>
            </button>

            {/* Popover */}
            {open && (
                <div
                    role="dialog"
                    aria-label={t('eod_cal_title')}
                    style={{
                        position: 'absolute',
                        top: 'calc(100% + 6px)',
                        right: 0,
                        width: 280,
                        maxWidth: 'calc(100vw - 32px)',
                        background: C.white,
                        border: `1px solid ${C.border}`,
                        borderRadius: 12,
                        boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
                        zIndex: 50,
                        padding: 12,
                        fontFamily: FONT,
                    }}
                >
                    {/* Header: prev / label / next */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                        <button
                            type="button"
                            onClick={goPrev}
                            aria-label={t('eod_cal_prev_month')}
                            style={navBtn}
                        >
                            ‹
                        </button>
                        <div style={{ fontSize: 13, fontWeight: 800, color: C.text }}>{monthLabel}</div>
                        <button
                            type="button"
                            onClick={goNext}
                            aria-label={t('eod_cal_next_month')}
                            style={navBtn}
                        >
                            ›
                        </button>
                    </div>

                    {/* Weekday header */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 4 }}>
                        {WEEKDAY_KEYS.map((wk) => (
                            <div
                                key={wk}
                                style={{
                                    textAlign: 'center',
                                    fontSize: 10,
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.5,
                                    color: C.mutedLight,
                                    padding: '4px 0',
                                }}
                            >
                                {t(wk)}
                            </div>
                        ))}
                    </div>

                    {/* Day grid */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
                        {cells.map((key, idx) => {
                            if (!key) return <div key={`b-${idx}`} />;
                            const dayNum = Number(key.slice(8, 10));
                            const enabled = available.has(key);
                            const selected = key === value;
                            return (
                                <button
                                    key={key}
                                    type="button"
                                    disabled={!enabled}
                                    aria-disabled={!enabled}
                                    aria-pressed={selected}
                                    onClick={() => handlePick(key)}
                                    onMouseEnter={(e) => {
                                        if (enabled && !selected) e.currentTarget.style.background = C.greenLight;
                                    }}
                                    onMouseLeave={(e) => {
                                        if (enabled && !selected) e.currentTarget.style.background = C.white;
                                    }}
                                    style={{
                                        position: 'relative',
                                        height: 34,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        border: 'none',
                                        borderRadius: 8,
                                        fontSize: 13,
                                        fontWeight: selected ? 800 : 600,
                                        fontFamily: FONT,
                                        background: selected ? C.green : C.white,
                                        color: selected ? C.white : enabled ? C.text : C.mutedLight,
                                        cursor: enabled ? 'pointer' : 'not-allowed',
                                        outlineColor: C.green,
                                    }}
                                >
                                    {dayNum}
                                    {/* availability dot */}
                                    {enabled && !selected && (
                                        <span
                                            aria-hidden="true"
                                            style={{
                                                position: 'absolute',
                                                bottom: 4,
                                                left: '50%',
                                                transform: 'translateX(-50%)',
                                                width: 4,
                                                height: 4,
                                                borderRadius: '50%',
                                                background: C.green,
                                            }}
                                        />
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

const navBtn = {
    width: 28,
    height: 28,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: C.white,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    fontSize: 16,
    lineHeight: 1,
    color: C.text,
    cursor: 'pointer',
    fontFamily: FONT,
};

export default BusinessDateCalendar;
